/**
 * Faculty Master — roster grouped by program within department
 */

import { Fragment, useEffect, useMemo } from 'react';
import { facultyMatchesHighlight } from './FacultyMasterFilters';

function StatusDot({ active }) {
  const on = active !== false;
  return (
    <span className={`student-status-dot ${on ? 'student-status-dot--on' : 'student-status-dot--off'}`}>
      {on ? 'Active' : 'Off'}
    </span>
  );
}

function FacultyRow({ f, highlightTerm }) {
  const highlighted = facultyMatchesHighlight(f, highlightTerm);
  const fullName = `${f.title ? `${f.title}. ` : ''}${f.first_name || ''} ${f.last_name || ''}`.trim();
  const qual = f.highest_qualification || f.qualification || '—';
  const exp = f.experience_years != null ? `${f.experience_years}y` : '—';

  return (
    <tr className={highlighted ? 'student-search-match' : undefined}>
      <td className="student-col-prn">
        <span className="student-roll-text" title={f.employee_id}>{f.employee_id || '—'}</span>
      </td>
      <td className="student-col-name">
        <span className="student-name-cell" title={fullName}>{fullName}</span>
      </td>
      <td className="student-col-email student-email" title={f.email}>{f.email || '—'}</td>
      <td className="student-col-dept student-dept-text">{f.department_code || f.department || '—'}</td>
      <td className="faculty-col-designation" title={f.designation}>{f.designation || '—'}</td>
      <td className="student-cell-muted faculty-col-qual" title={qual}>{qual}</td>
      <td className="faculty-col-research student-cell-muted" title={f.research_area || f.specialization}>
        {f.research_area || f.specialization || '—'}
      </td>
      <td className="student-col-phone student-cell-muted">{f.contact_number || '—'}</td>
      <td className="faculty-col-office student-cell-muted" title={f.office_location}>{f.office_location || '—'}</td>
      <td className="student-col-yr student-cell-num">{exp}</td>
      <td className="student-col-status"><StatusDot active={f.is_active} /></td>
    </tr>
  );
}

export default function FacultyMasterTable({
  faculty,
  rosterShown,
  highlightTerm,
  departmentLabel,
  programLabel,
  groupByProgram = true,
}) {
  const highlightCount = highlightTerm
    ? faculty.filter((f) => facultyMatchesHighlight(f, highlightTerm)).length
    : 0;

  const programGroups = useMemo(() => {
    if (!groupByProgram) {
      return [['', faculty]];
    }
    const map = new Map();
    for (const f of faculty) {
      const key = f.program_name || 'Unassigned program';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [faculty, groupByProgram]);

  useEffect(() => {
    if (!highlightTerm) return;
    const el = document.querySelector('.faculty-roster-table tbody tr.student-search-match');
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [highlightTerm, faculty]);

  if (!rosterShown) {
    return (
      <div className="student-roster-placeholder">
        <p className="student-roster-placeholder-title">No roster loaded</p>
        <p className="student-roster-placeholder-text">
          Select department and program (or <strong>All programs in department</strong>), then click <strong>Show</strong>.
        </p>
      </div>
    );
  }

  if (faculty.length === 0) {
    return (
      <div className="admin-empty py-10">
        <p className="admin-empty-title">No faculty match these filters</p>
        <p className="admin-empty-text">Try another program or clear optional semester/division filters.</p>
      </div>
    );
  }

  const colSpan = 11;

  return (
    <div className="student-roster-wrap">
      <div className="student-roster-summary faculty-roster-context">
        <div>
          <span className="faculty-roster-context-dept">
            Department: <strong>{departmentLabel || faculty[0]?.department_label || faculty[0]?.department || '—'}</strong>
          </span>
          {programLabel ? (
            <span className="faculty-roster-context-program">
              Program: <strong>{programLabel}</strong>
            </span>
          ) : (
            <span className="faculty-roster-context-program">
              {programGroups.length} program{programGroups.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <span>{faculty.length} faculty</span>
        {highlightTerm ? (
          <span className="student-roster-summary-highlight">
            {highlightCount} highlighted
          </span>
        ) : null}
      </div>
      <div className="table-wrap student-roster-table-wrap">
        <table className="table student-master-table student-roster-table student-roster-table--dense faculty-roster-table">
          <thead>
            <tr>
              <th className="student-col-prn">Emp. ID</th>
              <th className="student-col-name">Name</th>
              <th className="student-col-email">Email</th>
              <th className="student-col-dept">Dept</th>
              <th>Designation</th>
              <th>Qualification</th>
              <th className="faculty-col-research">Research</th>
              <th className="student-col-phone">Phone</th>
              <th className="faculty-col-office">Office</th>
              <th className="student-col-yr">Exp.</th>
              <th className="student-col-status">Status</th>
            </tr>
          </thead>
          <tbody>
            {programGroups.map(([programName, rows]) => (
              <Fragment key={programName || 'all'}>
                {groupByProgram ? (
                  <tr className="faculty-roster-group-row">
                    <td colSpan={colSpan}>
                      <span className="faculty-roster-group-title">{programName}</span>
                      <span className="faculty-roster-group-count">{rows.length} faculty</span>
                    </td>
                  </tr>
                ) : null}
                {rows.map((f) => (
                  <FacultyRow key={f.id} f={f} highlightTerm={highlightTerm} />
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
